import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../button'
import { Input } from '../input'
import { SelectWrapper } from '../select-wrapper'
import { CustomSelect } from '../custom-select'
import { Spinner } from '../spinner'
import { ConfirmDeleteDialog } from '../confirm-delete-dialog'
import { cn } from 'src/utils/style'
import {
  createVendorBankAccountApi,
  vendorBankAccountQueries,
  VendorBankAccount,
  accountTypeEnum,
  accountTypeLabelMap,
} from 'src/services/vendor-bank-accounts'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { SelectOption } from 'src/services/base-model'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'

interface VendorBankAccountsSectionProps {
  vendorId: string | null
}

const countryOptions: SelectOption[] = [
  { label: 'United States', value: 'US' },
  { label: 'Colombia', value: 'CO' },
  { label: 'Other', value: 'OTHER' },
]

const accountTypeOptions: SelectOption[] = Object.entries(accountTypeLabelMap).map(
  ([value, label]) => ({ label, value }),
)

interface BankAccountFormData {
  label: string
  country: string
  bankName: string
  bankCode: string
  accountNumber: string
  accountType: string
  currencyCode: string
  swiftBic: string
  iban: string
  isPrimary: boolean
  notes: string
}

const emptyForm: BankAccountFormData = {
  label: '',
  country: '',
  bankName: '',
  bankCode: '',
  accountNumber: '',
  accountType: accountTypeEnum.CHECKING,
  currencyCode: '',
  swiftBic: '',
  iban: '',
  isPrimary: false,
  notes: '',
}

interface FormErrors {
  label?: string
  country?: string
  bankName?: string
  bankCode?: string
  accountNumber?: string
  accountType?: string
  currencyCode?: string
  swiftBic?: string
}

function validateForm(form: BankAccountFormData, t: (key: string) => string): FormErrors {
  const errors: FormErrors = {}

  if (!form.label.trim()) errors.label = t('expenseForm.errors.required')
  if (!form.country) errors.country = t('expenseForm.errors.required')
  if (!form.bankName.trim()) errors.bankName = t('expenseForm.errors.required')
  if (!form.accountNumber.trim()) errors.accountNumber = t('expenseForm.errors.required')
  if (!form.accountType) errors.accountType = t('expenseForm.errors.required')
  if (!form.currencyCode.trim()) errors.currencyCode = t('expenseForm.errors.required')

  // Bank code required for US/CO
  if ((form.country === 'US' || form.country === 'CO') && !form.bankCode.trim()) {
    errors.bankCode = t('vendorBankAccounts.bankCodeRequired')
  }

  // US routing number must be 9 digits
  if (form.country === 'US' && form.bankCode.trim() && !/^\d{9}$/.test(form.bankCode.trim())) {
    errors.bankCode = t('vendorBankAccounts.routingNumberDigits')
  }

  // SWIFT/BIC validation: 8 or 11 chars
  if (form.swiftBic.trim() && ![8, 11].includes(form.swiftBic.trim().length)) {
    errors.swiftBic = t('vendorBankAccounts.swiftBicLength')
  }

  return errors
}

const BankAccountForm: React.FC<{
  form: BankAccountFormData
  errors: FormErrors
  onChange: (field: keyof BankAccountFormData, value: string | boolean) => void
  onSubmit: () => void
  onCancel: () => void
  isPending: boolean
  isEdit: boolean
}> = ({ form, errors, onChange, onSubmit, onCancel, isPending, isEdit }) => {
  const { t } = useTranslation()

  const bankCodeLabel =
    form.country === 'US' ? t('vendorBankAccounts.routingNumber') : t('vendorBankAccounts.bankCode')
  const bankCodePlaceholder =
    form.country === 'US'
      ? t('vendorBankAccounts.routingNumberPlaceholder')
      : t('vendorBankAccounts.bankCodePlaceholder')
  const showBankCode = form.country === 'US' || form.country === 'CO'
  const showSwiftBic = form.country !== 'US'

  const translatedCountryOptions = countryOptions.map((opt) => ({
    ...opt,
    label:
      opt.value === 'US'
        ? t('vendorBankAccounts.countryUS')
        : opt.value === 'CO'
        ? t('vendorBankAccounts.countryCO')
        : t('vendorBankAccounts.countryOther'),
  }))

  return (
    <div className="space-y-3 rounded-lg border border-border bg-surface-hover p-4">
      <h5 className="text-sm font-semibold text-gray-700">
        {isEdit ? t('vendorBankAccounts.editAccount') : t('vendorBankAccounts.addAccount')}
      </h5>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Input
            label={t('vendorBankAccounts.label')}
            placeholder={t('vendorBankAccounts.labelPlaceholder')}
            value={form.label}
            onChange={(e) => onChange('label', e.target.value)}
          />
          {errors.label && <p className="mt-1 text-xs text-error">{errors.label}</p>}
        </div>

        <div>
          <SelectWrapper label={t('vendorBankAccounts.country')}>
            <CustomSelect
              options={translatedCountryOptions}
              values={translatedCountryOptions.filter((o) => o.value === form.country)}
              onChange={(values) => onChange('country', values[0]?.value || '')}
              placeholder={t('vendorBankAccounts.countryPlaceholder')}
            />
          </SelectWrapper>
          {errors.country && <p className="mt-1 text-xs text-error">{errors.country}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Input
            label={t('vendorBankAccounts.bankName')}
            placeholder={t('vendorBankAccounts.bankNamePlaceholder')}
            value={form.bankName}
            onChange={(e) => onChange('bankName', e.target.value)}
          />
          {errors.bankName && <p className="mt-1 text-xs text-error">{errors.bankName}</p>}
        </div>

        {showBankCode && (
          <div>
            <Input
              label={bankCodeLabel}
              placeholder={bankCodePlaceholder}
              value={form.bankCode}
              onChange={(e) => onChange('bankCode', e.target.value)}
            />
            {errors.bankCode && <p className="mt-1 text-xs text-error">{errors.bankCode}</p>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Input
            label={t('vendorBankAccounts.accountNumber')}
            placeholder={t('vendorBankAccounts.accountNumberPlaceholder')}
            value={form.accountNumber}
            onChange={(e) => onChange('accountNumber', e.target.value)}
          />
          {errors.accountNumber && (
            <p className="mt-1 text-xs text-error">{errors.accountNumber}</p>
          )}
        </div>

        <div>
          <SelectWrapper label={t('vendorBankAccounts.accountType')}>
            <CustomSelect
              options={accountTypeOptions}
              values={accountTypeOptions.filter((o) => o.value === form.accountType)}
              onChange={(values) => onChange('accountType', values[0]?.value || '')}
              placeholder={t('vendorBankAccounts.accountTypePlaceholder')}
            />
          </SelectWrapper>
          {errors.accountType && <p className="mt-1 text-xs text-error">{errors.accountType}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Input
            label={t('vendorBankAccounts.currencyCode')}
            placeholder={t('vendorBankAccounts.currencyCodePlaceholder')}
            value={form.currencyCode}
            onChange={(e) => onChange('currencyCode', e.target.value.toUpperCase())}
          />
          {errors.currencyCode && <p className="mt-1 text-xs text-error">{errors.currencyCode}</p>}
        </div>

        {showSwiftBic && (
          <div>
            <Input
              label={t('vendorBankAccounts.swiftBic')}
              placeholder={t('vendorBankAccounts.swiftBicPlaceholder')}
              value={form.swiftBic}
              onChange={(e) => onChange('swiftBic', e.target.value.toUpperCase())}
            />
            {errors.swiftBic && <p className="mt-1 text-xs text-error">{errors.swiftBic}</p>}
          </div>
        )}
      </div>

      <div>
        <Input
          label={t('vendorBankAccounts.iban')}
          placeholder={t('vendorBankAccounts.ibanPlaceholder')}
          value={form.iban}
          onChange={(e) => onChange('iban', e.target.value.toUpperCase())}
        />
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={form.isPrimary}
          onChange={(e) => onChange('isPrimary', e.target.checked)}
          className="h-4 w-4 rounded border-border-strong text-primary accent-primary"
        />
        {t('vendorBankAccounts.isPrimary')}
      </label>

      <div>
        <label className="mb-1 block text-sm font-medium text-primary">
          {t('vendorBankAccounts.notes')}
        </label>
        <textarea
          className="w-full rounded-md border border-border bg-surface p-2 text-sm"
          rows={2}
          placeholder={t('vendorBankAccounts.notesPlaceholder')}
          value={form.notes}
          onChange={(e) => onChange('notes', e.target.value)}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="button" variant="primary" onClick={onSubmit} disabled={isPending}>
          {isPending ? t('vendorBankAccounts.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  )
}

export const VendorBankAccountsSection: React.FC<VendorBankAccountsSectionProps> = ({
  vendorId,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<BankAccountFormData>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>({})
  const { data, isLoading } = useQuery({
    ...vendorBankAccountQueries.list({ vendorId: vendorId || '' }),
    enabled: Boolean(vendorId),
  })

  const accounts: VendorBankAccount[] = data?.results || []

  const createMutation = useMutation({
    mutationFn: (payload: any) => {
      const api = createVendorBankAccountApi(vendorId!)
      return api.create(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorBankAccountQueries.all(vendorId!) })
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }: any) => {
      const api = createVendorBankAccountApi(vendorId!)
      return api.update({ id, ...payload })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorBankAccountQueries.all(vendorId!) })
      resetForm()
    },
  })

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: (id: string) => {
      const api = createVendorBankAccountApi(vendorId!)
      return api.remove(id)
    },
    queryKey: vendorBankAccountQueries.all(vendorId!),
  })

  const resetForm = () => {
    setShowForm(false)
    setEditingId(null)
    setForm(emptyForm)
    setErrors({})
  }

  const handleChange = (field: keyof BankAccountFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field as keyof FormErrors]
        return next
      })
    }
  }

  const handleSubmit = () => {
    const validationErrors = validateForm(form, t)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    const payload = {
      label: form.label,
      country: form.country === 'OTHER' ? '' : form.country,
      bankName: form.bankName,
      bankCode: form.bankCode || null,
      accountNumber: form.accountNumber,
      accountType: form.accountType,
      currencyCode: form.currencyCode,
      swiftBic: form.swiftBic || null,
      iban: form.iban || null,
      isPrimary: form.isPrimary,
      notes: form.notes || null,
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const handleEdit = (account: VendorBankAccount) => {
    setEditingId(account.id)
    setForm({
      label: account.label,
      country: account.country || 'OTHER',
      bankName: account.bankName,
      bankCode: account.bankCode || '',
      accountNumber: '',
      accountType: account.accountType,
      currencyCode: account.currencyCode,
      swiftBic: account.swiftBic || '',
      iban: account.iban || '',
      isPrimary: account.isPrimary,
      notes: account.notes || '',
    })
    setErrors({})
    setShowForm(true)
  }

  // Show placeholder in create mode (before vendor is saved)
  if (!vendorId) {
    return (
      <div className="rounded-xl border border-border bg-surface p-4">
        <h4 className="text-sm font-semibold text-gray-700">{t('vendorBankAccounts.title')}</h4>
        <p className="mt-2 text-sm text-text-placeholder">{t('vendorBankAccounts.noAccounts')}</p>
      </div>
    )
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">{t('vendorBankAccounts.title')}</h4>
        {!showForm && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setForm(emptyForm)
              setEditingId(null)
              setErrors({})
              setShowForm(true)
            }}
            className="text-sm"
          >
            <PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('vendorBankAccounts.addAccount')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : accounts.length === 0 && !showForm ? (
        <p className="text-sm text-text-placeholder">{t('vendorBankAccounts.noAccounts')}</p>
      ) : (
        <div className="space-y-2">
          {accounts.map((account) => (
            <div
              key={account.id}
              className={cn(
                'flex items-center justify-between rounded-lg border px-4 py-3',
                account.isActive
                  ? 'border-border bg-surface'
                  : 'border-border bg-surface opacity-60',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-text-primary">{account.label}</span>
                  {account.isPrimary && (
                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary">
                      {t('vendorBankAccounts.primary')}
                    </span>
                  )}
                  {!account.isActive && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                      {t('common.inactive')}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-secondary">
                  <span>{account.bankName}</span>
                  <span>
                    {t('vendorBankAccounts.maskedAccount', {
                      lastFour: account.maskedAccountNumber.slice(-4),
                    })}
                  </span>
                  <span>{account.currencyCode}</span>
                  {account.country && <span>{account.country}</span>}
                </div>
              </div>

              <div className="ml-4 flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  tone="neutral"
                  iconOnly
                  size="sm"
                  shape="square"
                  onClick={() => handleEdit(account)}
                  aria-label={t('common.edit')}
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  tone="error"
                  iconOnly
                  size="sm"
                  shape="square"
                  onClick={() => requestDelete(account.id, account.label)}
                  aria-label={t('common.delete')}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="mt-3">
          <BankAccountForm
            form={form}
            errors={errors}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onCancel={resetForm}
            isPending={isPending}
            isEdit={Boolean(editingId)}
          />
        </div>
      )}

      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  )
}
